/*************************************************
  1Store — Auto image fit v7
  Автоматически приводит товары к единому визуальному размеру и
  центрирует их: меряет реальные границы товара (непустые пиксели)
  и выставляет per-image переменные --img-zoom / --img-dx / --img-dy.

  Зоны действия (CONTEXTS):
  - карточки каталога:  ul.l4cl li.product-card figure
    (обе картинки: основная И ховерная — каждая со своим расчётом)
  - галерея карточки товара: .m6pr .l4pr > li
    (миниатюры-«буллеты» не трогаем)

  Приоритеты: автоматика главнее метаполя custom.image_zoom
  (метаполе на figure остаётся запасным: работает через наследование
  CSS-переменной, когда анализ невозможен — серый фон и т.п.).

  Экономия: анализ по миниатюре width=180 (~3-8 КБ), результат в
  localStorage по имени файла (замена фото в Shopify = новое имя =
  свежий анализ, протухнуть не может). Расчёт от offset-размеров и
  натуральных пикселей — transform их не искажает, петли нет.

  iOS Safari: запрос анализа идёт с отдельными URL-параметрами,
  иначе Safari отдаёт кэш без CORS-заголовков и canvas блокируется.

  Отладка: открыть страницу с ?imgfit-debug — плашки на карточках.
**************************************************/
(() => {
  const TARGET = 0.9;          // товар должен занимать ~90% бокса
  const MIN = 0.8, MAX = 2.0;  // пределы авто-зума
  const SKIP_DELTA = 0.03;     // если и так почти идеально — не трогаем
  const SAMPLE = 96;           // ширина канвы для анализа (пикс)
  const CACHE = 'imgbox2:';
  const CONTEXTS = 'ul.l4cl li.product-card figure, .m6pr .l4pr li:not(.more)';

  const analyze = (src) => new Promise((resolve) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      try {
        const w = SAMPLE;
        const h = Math.max(1, Math.round((w * im.naturalHeight) / im.naturalWidth));
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(im, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (d[i + 3] > 20 && !(d[i] > 244 && d[i + 1] > 244 && d[i + 2] > 244)) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) return resolve(null);
        resolve({
          fw: Math.round(((maxX - minX + 1) / w) * 1000) / 1000,
          fh: Math.round(((maxY - minY + 1) / h) * 1000) / 1000,
          cx: Math.round((((minX + maxX + 1) / 2) / w) * 1000) / 1000,
          cy: Math.round((((minY + maxY + 1) / 2) / h) * 1000) / 1000,
        });
      } catch (e) {
        resolve(null);
      }
    };
    im.onerror = () => resolve(null);
    if (src.indexOf('/cdn/shop/') > -1) {
      im.src = src.split('?')[0] + '?width=180&imgfit=cors';
    } else {
      im.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'imgfit=cors';
    }
  });

  const cacheKey = (src) => CACHE + src.split('?')[0].split('/').pop();

  async function getBox(src) {
    const k = cacheKey(src);
    try {
      const c = JSON.parse(localStorage.getItem(k));
      if (c && c.fw > 0 && c.fh > 0) return c;
    } catch (e) {}
    const a = await analyze(src);
    if (!a) return null;
    try { localStorage.setItem(k, JSON.stringify(a)); } catch (e) {}
    return a;
  }

  function applyZoom(container, img, box) {
    const natW = img.naturalWidth, natH = img.naturalHeight;
    if (!natW || !natH) return;

    const pic = img.closest('picture') || img.parentElement;
    const boxW = container.offsetWidth, boxH = container.offsetHeight;
    const picW = pic ? pic.offsetWidth : boxW, picH = pic ? pic.offsetHeight : boxH;
    const imgW = img.offsetWidth, imgH = img.offsetHeight;
    if (!boxW || !boxH || !picW || !picH || !imgW || !imgH) return;

    // видимая область = самый тесный контейнер с обрезкой
    const clipW = Math.min(boxW, picW);
    const clipH = Math.min(boxH, picH);

    // как object-fit:contain отрисует картинку внутри её элемента
    const paint = Math.min(imgW / natW, imgH / natH);
    const contentW = natW * paint * box.fw;
    const contentH = natH * paint * box.fh;
    if (!contentW || !contentH) return;

    const fill = Math.max(contentW / clipW, contentH / clipH);
    if (!fill) return;
    img.dataset.imgfitFill = fill.toFixed(2);

    // на первом фото карточки товара с габаритами оставляем запас
    // под размерные линии (product-dimensions.js)
    let target = TARGET;
    if (container.closest && container.closest('.l4pr[data-dims-h]')) target = 0.84;

    let zoom = target / fill;
    zoom = Math.min(zoom, MAX);
    zoom = Math.max(zoom, MIN);
    zoom = Math.round(zoom * 100) / 100;
    if (Math.abs(zoom - 1) <= SKIP_DELTA) zoom = 1;

    // центрирование товара, смещённого в кадре
    const paintedW = natW * paint, paintedH = natH * paint;
    let dx = 0, dy = 0;
    if (box.cx && box.cy) {
      dx = Math.round(-(box.cx - 0.5) * paintedW * zoom);
      dy = Math.round(-(box.cy - 0.5) * paintedH * zoom);
    }

    const sig = zoom + '|' + dx + '|' + dy;
    if (img.dataset.imgfitSig === sig) return;
    img.dataset.imgfitSig = sig;
    img.dataset.imgfitZoom = String(zoom);

    // переменные на САМОМ img: у основной и ховерной картинки — свои,
    // а метаполе на figure остаётся наследуемым запасным значением
    img.style.setProperty('--img-zoom', zoom);
    img.style.setProperty('--img-dx', dx + 'px');
    img.style.setProperty('--img-dy', dy + 'px');

    // при увеличении подсказываем браузеру догрузить чёткий вариант
    if (zoom > 1.05 && img.getAttribute('sizes')) {
      const want = Math.ceil(imgW * zoom);
      const cur = parseInt(img.dataset.imgfitSizes || '0');
      if (want > cur) {
        img.dataset.imgfitSizes = String(want);
        img.sizes = want + 'px';
      }
    }
  }

  /* отладка: страница с ?imgfit-debug — плашки со статусом */
  const DEBUG = location.search.indexOf('imgfit-debug') > -1;
  function mark(container, text, bad) {
    if (!DEBUG) return;
    let el = container.querySelector(':scope > .imgfit-debug');
    if (!el) {
      el = document.createElement('span');
      el.className = 'imgfit-debug';
      el.style.cssText = 'position:absolute;left:4px;bottom:4px;z-index:99;font:700 11px/1.6 sans-serif;padding:0 7px;border-radius:6px;background:#0e7a3d;color:#fff;pointer-events:none';
      container.style.position = 'relative';
      container.appendChild(el);
    }
    el.style.background = bad ? '#c8382e' : '#0e7a3d';
    el.textContent = text;
  }

  function eligible(img) {
    if (!img || img.tagName !== 'IMG') return null;
    if (img.closest('.swiper-pagination-bullets')) return null; // миниатюры галереи
    return img.closest(CONTEXTS);
  }

  async function fit(container, img) {
    const src = img.currentSrc || img.src;
    if (!src || !img.complete || !img.naturalWidth) return;
    if (img.dataset.imgfitSrc === src && img.dataset.imgfitSig) return;
    img.dataset.imgfitSrc = src;

    const box = await getBox(src);
    if (!box) {
      const manual = parseFloat(getComputedStyle(container).getPropertyValue('--img-zoom')) || 1;
      mark(container, Math.abs(manual - 1) > 0.001 ? 'manual ' + manual : 'ERR analyze', Math.abs(manual - 1) <= 0.001);
      return; // остаётся наследуемое значение метаполя (или 1)
    }
    img.dataset.imgfitBox = JSON.stringify(box);
    applyZoom(container, img, box);
    if (container.querySelector('img') === img) {
      mark(container, 'fill ' + (img.dataset.imgfitFill || '?') + ' → ×' + (img.dataset.imgfitZoom || '1'));
    }
  }

  document.addEventListener('load', (e) => {
    const container = eligible(e.target);
    if (container) fit(container, e.target);
  }, true);

  function sweep() {
    document.querySelectorAll(CONTEXTS).forEach((container) => {
      container.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth && eligible(img) === container) fit(container, img);
      });
    });
  }

  /* пересчёт при смене раскладки (поворот телефона, ресайз окна) */
  let rt = null;
  window.addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      document.querySelectorAll('img[data-imgfit-box]').forEach((img) => {
        const container = eligible(img);
        if (!container || !img.complete) return;
        try { applyZoom(container, img, JSON.parse(img.dataset.imgfitBox)); } catch (e) {}
      });
    }, 200);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
  // §23.447: опрос раз в секунду двадцать раз подряд шёл до конца, даже
  // когда подгонять уже нечего: каждый проход обходит все картинки сетки, а
  // на странице коллекции их полсотни. Событие `load` в фазе перехвата
  // (выше) и так ловит картинки по мере загрузки; опрос — страховка для тех,
  // что вставлены уже готовыми (клоны свайпера, возврат из bfcache).
  //
  // Выключаем страховку, когда три прохода подряд ничего не меняют — но
  // только если хотя бы одна картинка уже подогнана. Иначе на медленной
  // сети опрос умер бы раньше, чем первая картинка успела загрузиться.
  let sweeps = 0, idle = 0;
  const fitted = () => document.querySelectorAll('img[data-imgfit-sig]').length;
  const t = setInterval(() => {
    const before = fitted();
    sweep();
    const after = fitted();
    idle = (after === before) ? idle + 1 : 0;
    if (++sweeps >= 20 || (idle >= 3 && after > 0)) clearInterval(t);
  }, 1000);
  document.addEventListener('visibilitychange', sweep);
})();